import React, { useState, useEffect } from "react";
import { Box, Paper, Button, Divider, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setPage, setRowsPerPage } from "../../store/slices/paginationSlice";
import TenantFilters from "./TenantFilters";
import TenantTable from "./TenantTable";
import PaginationManager from "../../components/newComp/Pagination";
import { showToast } from "../../store/slices/toastSlice";
import { fetchTenantList } from "../../services/api/tenant";
import { fetchBuildingList } from "../../services/api/building";

const TenantList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { page, rowsPerPage } = useSelector((state) => state.pagination);

  const [tenantList, setTenantList] = useState([]);
  const [filteredTenantList, setFilteredTenantList] = useState([]);
  const [buildingList, setBuildingList] = useState([]);
  const [selectBuildings, setSelectBuildings] = useState([]);

  // Fetch tenant list data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const tenantData = await fetchTenantList();
        setTenantList(tenantData.data);
        setFilteredTenantList(tenantData.data); // Set both lists initially

        const buildingData = await fetchBuildingList(); // Fetch all building
        setBuildingList(buildingData.data);
      } catch (error) {
        dispatch(showToast({ message: error.message, severity: "error" }));
      }
    };
    fetchInitialData();
  }, [dispatch]);

  useEffect(() => {
    // Filter tenants based on selected building names
    if (selectBuildings.length > 0) {
      const selectedBuildingNames = selectBuildings.map(
        (building) => building.building_name // Extract the building names from the selected buildings
      );

      const filteredData = tenantList.filter(
        (tenant) => selectedBuildingNames.includes(tenant.building_name) // Compare tenant's building_name with selected building names
      );
      setFilteredTenantList(filteredData);
    } else {
      // If no buildings are selected, show all tenants
      setFilteredTenantList(tenantList);
    }
  }, [selectBuildings, tenantList]); // Re-run whenever selectBuildings or tenantList changes

  // Pagination logic
  const handleChangePage = (event, newPage) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
    dispatch(setPage(0)); // Reset to page 0 when rows per page changes
  };

  // Slice filtered data based on current page and rows per page
  const currentPageData = filteredTenantList.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <>
      <Box
        sx={{ width: "100%", p: { xs: 2, md: 3 }, backgroundColor: "#f7f8fa" }}
      >
        {/* Filter Section */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            mb: 3,
            p: 2.5,
            borderRadius: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
          }}
        >
          <TenantFilters
            buildingList={buildingList} // Pass all tenants to filters
            selectBuildings={selectBuildings}
            setSelectBuildings={setSelectBuildings} // Update selected buildings
            handleExport={() => {}}
          />
        </Paper>

        {/* Top Action Bar */}
        {/* Top Bar: Title + Add Button */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333" }}>
              Tenant List
            </Typography>
          </Box>

          <Button
            variant="contained"
            sx={{
              backgroundColor: "#024ad7",
              minHeight: 40,
              textTransform: "none",
              fontWeight: 600,
              borderRadius: "8px",
              px: 3,
              "&:hover": {
                backgroundColor: "#fec01f",
                color: "#000",
              },
            }}
            onClick={() =>
              navigate("/tenant", {
                state: { from: "/tenant-list" },
              })
            }
          >
            + Add New Tenant
          </Button>
        </Box>

        {/* Table Section */}
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: 2,
            backgroundColor: "#fff",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              backgroundColor: "#fff",
              borderRadius: 2,
              p: 2,
            }}
          >
            <TenantTable rows={currentPageData} />
          </Box>

          <Divider />

          <PaginationManager
            count={filteredTenantList.length} // Total count of filtered data
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </Box>
    </>
  );
};

export default TenantList;
